import { ConfigStore } from "../lib/config/config";
import { IEventEmitter, IEventSource } from "../lib/eventing/events";
import { ExcelEvents, ExcelEventSource, Workbook } from "./excelEvents";

export type WorkbookClassificationEvents = {
    workbookClassified: WorkbookClassification & { workbookId:string, restored:boolean },
    workbookUnclassified: { considered: string[] }
    workbookClassificationConflict: { claimed: string[], considered:string[]}
}

export type WorkbookClassificationEmitter = IEventEmitter<WorkbookClassificationEvents>;
export type WorkbookClassificationSource = IEventSource<WorkbookClassificationEvents>;

export type WorkbookClassification = {
    timestamp: number;
    code: string;
    rule: string;
    considered: string[];
}
export type ClassificationRule<TRule extends string = string> = {
    name: TRule;
    description: string;
    looseCriteria: (workbook: Workbook) => boolean;
    tightCriteria: (workbook: Workbook) => boolean;
}
export type ClassificationRuleMap = Record<string, ClassificationRule>;

export type ClassificationStore = ConfigStore<WorkbookClassification>;

export class WorkbookClassifier {
    constructor(
        private readonly classificationRules: ClassificationRuleMap,
        private readonly appEvents:ExcelEventSource,
        private readonly classificationEmitter: WorkbookClassificationEmitter,
        private readonly classificationStore: ClassificationStore
    ) {
        this.appEvents.events.workbookOpened.on(async (payload) => await this.handleWorkbookOpened(payload));
    }

    private async handleWorkbookOpened(payload: ExcelEvents['workbookOpened']) {
        const existingClassification = await this.loadExistingClassification(payload.workbookId);
        if (!existingClassification) await this.evaluateClassificationRules(payload.workbook);
    }

    private async loadExistingClassification(workbookId: string): Promise<WorkbookClassification | null> {
        const classification = await this.classificationStore.load();
        if (classification) {
            this.emitWorkbookClassification(workbookId, classification, true);
        }
        return classification ?? null;
    }

    private async evaluateClassificationRules(workbook: Workbook) {
        const applicableClassifiers = Object.entries(this.classificationRules).filter(([_, rule]) => rule.looseCriteria(workbook))
        const applicableCodes = applicableClassifiers.map(([code, _]) => code);

        const claimingClassifiers = applicableClassifiers.filter(([_code, rule]) => rule.tightCriteria(workbook))
        const claimingCodes = claimingClassifiers.map(([code, _]) => code);

        if (claimingCodes.length > 1) {
            this.classificationEmitter.events.workbookClassificationConflict.emit({
                claimed: claimingCodes,
                considered: applicableCodes
            });
            return;
        }
        if (claimingCodes.length === 0) {
            this.classificationEmitter.events.workbookUnclassified.emit({
                considered: applicableCodes
            });
            return;
        }

        const claimingCode = claimingCodes[0];
        const claimingRule = this.classificationRules[claimingCode];
        const classification = {
            timestamp: Date.now(),
            code: claimingCode,
            rule: claimingRule.name,
            considered: applicableCodes
        }
        await this.classificationStore.save(classification);

        this.emitWorkbookClassification(workbook.id, classification, false);
    }

    private emitWorkbookClassification(workbookId:string, classification:WorkbookClassification, restored: boolean) {
        this.classificationEmitter.events.workbookClassified.emit({ workbookId, ...classification, restored });
    }
}